import { Info } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * The phone's map credit: a small round "i" at the top left, closed until asked for, in place of
 * MapLibre's own control, which opens by itself on some sizes and stays open through a tap on the
 * map. The words and links are what that control drew: its own for MapLibre, and the attribution
 * of the tiles OpenFreeMap serves (MAP_STYLE's source), so they change if the style ever does.
 *
 * Open, the next tap anywhere else closes it and does nothing more. It is stopped on its way down
 * through the page, before it reaches a button or MapLibre, which hears its taps as clicks on its
 * container: so it is neither a tap on the empty map nor one on a zone, a pin or a card. A finger
 * that drags makes no click: the map pans under an open credit.
 *
 * Holds its own state, so the layer's `key` closes it on a switch, as leaving the map does.
 */
export function MapCredit({
  below,
  crime,
}: {
  /**
   * What stands above it at the top of the map, for the stylesheet; nothing is the very top.
   * "chips" is the search bar and the chips together, "search" the bar on its own — a trip with no
   * places has no chips, and an offset for a row that is not drawn leaves this floating.
   */
  below?: "days" | "chips" | "search" | undefined;
  /** The crime data's short credit, where the map is painted with it. */
  crime?: string | null | undefined;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const tapped = (e: MouseEvent) => {
      if (e.target instanceof Element && e.target.closest(".map-credit")) return;
      e.stopPropagation();
      e.preventDefault();
      setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", tapped, true);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("click", tapped, true);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  return (
    <div className="map-credit" data-below={below}>
      <button
        type="button"
        className="map-credit-button"
        aria-label="Map credits"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">
          <Info size={18} strokeWidth={2} />
        </span>
      </button>
      {open && (
        <div className="map-credit-text">
          <p>
            <a href="https://maplibre.org/" target="_blank" rel="noreferrer">
              MapLibre
            </a>{" "}
            <a href="https://openfreemap.org" target="_blank" rel="noreferrer">
              OpenFreeMap
            </a>{" "}
            <a href="https://www.openmaptiles.org/" target="_blank" rel="noreferrer">
              © OpenMapTiles
            </a>{" "}
            Data from{" "}
            <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
              OpenStreetMap
            </a>
          </p>
          {crime && <p>{crime}</p>}
        </div>
      )}
    </div>
  );
}
